/**
 * What a person picked, as the images it makes: the slides of several files
 * (a MIRAX slide with its data folder, mirax.ts; the DICOM files of one
 * series, dicomSlides.ts) and every other file as it was. One way in for
 * both upload pages.
 */

import { slidesOf, type Picked } from './dicomSlides';
import { miraxOf } from './mirax';

export interface GroupedSlides {
  picked: Picked[];
  /** A .mrxs dropped without its data folder: said to the person, not sent. */
  loneMirax: File[];
  /** A .mrxs whose data folder came without its Slidedat.ini: said to the person (noSlidedatReason), not sent. */
  noSlidedat: File[];
}

/** What a MIRAX slide needs, in the person's words. */
export const LONE_MRXS = 'A MIRAX slide is the .mrxs file and its folder: drop the folder holding both, or a .zip of them.';

/**
 * Whether this browser keeps .ini files out of a dropped folder: Chrome and
 * Edge on Windows, which walk it by handles (fileHandles.missedByHandles), as
 * the browser says of itself. Firefox and Safari hand every file over.
 */
export function hidesIni(): boolean {
  if (typeof navigator === 'undefined' || typeof DataTransferItem === 'undefined') return false;
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
  const windows = platform ? platform === 'Windows' : /Windows/.test(navigator.userAgent);
  return windows && 'getAsFileSystemHandle' in DataTransferItem.prototype;
}

/**
 * Why `count` MIRAX slides whose folder came without its Slidedat.ini are not
 * sent, in the person's words, after their names. Where the browser held it
 * back (hidesIni), a .zip gets the slide past that; elsewhere the folder itself
 * is short of it.
 */
export function noSlidedatReason(count: number, heldBack = hidesIni()): string {
  const one = count === 1;
  return heldBack
    ? `Windows browsers hold back the Slidedat.ini file ${one ? 'this slide needs' : 'these slides need'}. Zip ${one ? 'the .mrxs file' : 'each .mrxs file'} and its folder together and drop the .zip.`
    : `${one ? 'its folder has' : 'their folders have'} no Slidedat.ini, and a MIRAX slide can't be read without it.`;
}

export async function groupSlides(files: readonly File[], accept: { dcm: boolean; mrxs: boolean }): Promise<GroupedSlides> {
  const mirax = accept.mrxs ? miraxOf(files) : { slides: [], lone: [], noSlidedat: [], rest: [...files] };
  const rest = accept.dcm ? await slidesOf(mirax.rest) : mirax.rest;
  return { picked: [...mirax.slides, ...rest], loneMirax: mirax.lone, noSlidedat: mirax.noSlidedat };
}
